import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldCheck, Storefront, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { EmptyState, BulkActionBar } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function QueueCard({ item, type, onVerify }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-[#0c0c14] border border-white/[0.06] rounded-lg hover:border-white/[0.1] transition-colors"
      data-testid={`verification-${type}-${item.id}`}>
      <div className="flex items-center gap-3">
        {type === 'shop' ? <Storefront size={18} weight="duotone" className="text-[#F59E0B]" /> : <Buildings size={18} weight="duotone" className="text-[#3B82F6]" />}
        <div>
          <p className="text-[13px] text-white font-medium">{item.name}</p>
          <p className="text-[10px] text-[#444]">
            {item.slug} {item.location ? `· ${item.location}` : ''} {item.website_url ? `· ${item.website_url}` : ''}
          </p>
          <p className="text-[10px] text-[#333] mt-0.5">
            Submitted {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'recently'}
          </p>
        </div>
      </div>
      <button onClick={() => onVerify(item.id)}
        className="text-[12px] px-4 py-1.5 rounded-md bg-[#10B981]/10 text-[#10B981] font-medium hover:bg-[#10B981]/20 transition-colors"
        data-testid={`verify-${type}-${item.id}`}>
        Verify
      </button>
    </div>
  );
}

export default function AdminVerification() {
  const [shops, setShops] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin-console/verification/shops`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin-console/verification/publishers`, { withCredentials: true }),
      ]);
      setShops(sRes.data || []); setPubs(pRes.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const verifyShop = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/shops/${id}/verify`, {}, { withCredentials: true });
      toast.success('Shop verified'); fetch();
    } catch { toast.error('Failed'); }
  };

  const verifyPub = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/publishers/${id}/verify`, {}, { withCredentials: true });
      toast.success('Publisher verified'); fetch();
    } catch { toast.error('Failed'); }
  };

  const bulkVerifyShops = async () => {
    try {
      await axios.post(`${API_URL}/api/admin-console/bulk/verify-shops`, { ids: shops.map(s => s.id) }, { withCredentials: true });
      toast.success('All shops verified'); fetch();
    } catch { toast.error('Failed'); }
  };

  const bulkVerifyPubs = async () => {
    try {
      await axios.post(`${API_URL}/api/admin-console/bulk/verify-publishers`, { ids: pubs.map(p => p.id) }, { withCredentials: true });
      toast.success('All publishers verified'); fetch();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white/[0.03] rounded-lg animate-pulse" />)}</div>;

  return (
    <div data-testid="admin-verification-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Verification Queue</h1>
        <p className="text-[13px] text-[#444] mt-0.5">{shops.length + pubs.length} items pending verification</p>
      </div>

      {/* Shops Queue */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Storefront size={15} weight="duotone" className="text-[#F59E0B]" />
            <h2 className="text-[14px] font-semibold text-white">Shops</h2>
            <span className="text-[11px] text-[#444] tabular-nums">({shops.length})</span>
          </div>
          {shops.length > 1 && (
            <button onClick={bulkVerifyShops}
              className="text-[11px] px-3 py-1.5 rounded-md bg-[#10B981]/10 text-[#10B981] font-medium"
              data-testid="bulk-verify-all-shops">Verify All</button>
          )}
        </div>
        {shops.length === 0 ? (
          <div className="text-[12px] text-[#333] px-4 py-4 bg-[#0c0c14] border border-white/[0.06] rounded-lg text-center">All shops verified</div>
        ) : (
          <div className="space-y-2">
            {shops.map(s => <QueueCard key={s.id} item={s} type="shop" onVerify={verifyShop} />)}
          </div>
        )}
      </div>

      {/* Publishers Queue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Buildings size={15} weight="duotone" className="text-[#3B82F6]" />
            <h2 className="text-[14px] font-semibold text-white">Publishers</h2>
            <span className="text-[11px] text-[#444] tabular-nums">({pubs.length})</span>
          </div>
          {pubs.length > 1 && (
            <button onClick={bulkVerifyPubs}
              className="text-[11px] px-3 py-1.5 rounded-md bg-[#10B981]/10 text-[#10B981] font-medium"
              data-testid="bulk-verify-all-pubs">Verify All</button>
          )}
        </div>
        {pubs.length === 0 ? (
          <div className="text-[12px] text-[#333] px-4 py-4 bg-[#0c0c14] border border-white/[0.06] rounded-lg text-center">All publishers verified</div>
        ) : (
          <div className="space-y-2">
            {pubs.map(p => <QueueCard key={p.id} item={p} type="publisher" onVerify={verifyPub} />)}
          </div>
        )}
      </div>
    </div>
  );
}
